import { startTransition, useEffect, useRef, useState } from "react";
import { api, useData, date, time, eventURL } from "../lib/events";
import { State } from "./Shows";
import { Login } from "./Login";
type Any = Record<string, any>;
const owner = "/api/owner";
function localTime(value: string, tz: string) {
  if (!value) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  const get = (key: string) => parts.find((p) => p.type === key)?.value;
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}
function utcTime(value: string, tz: string) {
  if (!value) return "";
  let stamp = Date.parse(value + "Z");
  for (let i = 0; i < 3; i++) {
    const actual = localTime(new Date(stamp).toISOString(), tz);
    stamp += Date.parse(value + "Z") - Date.parse(actual + "Z");
  }
  if (localTime(new Date(stamp).toISOString(), tz) !== value)
    throw Error(
      "That local time does not exist because of daylight saving. Choose another time.",
    );
  return new Date(stamp).toISOString();
}
async function copy(value: string) {
  await navigator.clipboard.writeText(value);
}
export function Manage() {
  const { data: session, error: sessionError } = useData("/api/session"),
    [shows, setShows] = useState<any[]>([]),
    [selected, setSelected] = useState<number | string | null>(null),
    [bundle, setBundle] = useState<Any | null>(null),
    [tab, setTab] = useState("details"),
    [error, setError] = useState(""),
    [feedback, setFeedback] = useState(""),
    [busy, setBusy] = useState(false),
    [switching, setSwitching] = useState(false);
  const requestVersion = useRef(0);
  const selectionVersion = useRef(0);
  async function load(id: number | string | null = selected, resetTab = false) {
    const version = ++requestVersion.current;
    // Keep the current editor mounted until its replacement is ready.
    // For a known show, fetch all independent data in the same round trip.
    const details =
      id !== null && id !== "new"
        ? Promise.all([
            api(owner + "/shows/" + id),
            api(owner + "/shows/" + id + "/settings"),
            api(owner + "/applications?event_id=" + id),
            api(owner + "/slots?event_id=" + id),
          ])
        : null;
    try {
      const [[events, venues], ready] = await Promise.all([
        Promise.all([api(owner + "/shows"), api(owner + "/venues")]),
        details,
      ]);
      const target = id ?? events[0]?.id ?? "new";
      let next: Any;
      if (target === "new") {
        next = {
          event: {
            title: "",
            timezone: "Europe/Madrid",
            status: "draft",
            visibility: "public",
            capacity: 0,
          },
          settings: { applications_open: true, set_minutes: 5, performer_capacity: 10 },
          venues,
          applications: [],
          slots: [],
        };
      } else {
        const [event, settings, applications, slots] =
          ready ??
          (await Promise.all([
            api(owner + "/shows/" + target),
            api(owner + "/shows/" + target + "/settings"),
            api(owner + "/applications?event_id=" + target),
            api(owner + "/slots?event_id=" + target),
          ]));
        next = {
          event,
          settings,
          applications,
          slots: slots.filter((slot: Any) => slot.status !== "cancelled"),
          venues,
        };
      }
      if (version !== requestVersion.current) return;
      startTransition(() => {
        setShows(events);
        setSelected(target);
        setBundle(next);
        if (resetTab) {
          setTab("details");
          setSwitching(false);
        }
      });
    } catch (error) {
      if (version === requestVersion.current) throw error;
    }
  }
  useEffect(() => {
    if (session?.owner) load().catch((e) => setError(e.message));
    return () => {
      requestVersion.current++;
    };
  }, [session]);
  async function select(id: number | string) {
    const version = ++selectionVersion.current;
    setSwitching(true);
    setError("");
    setFeedback("");
    try {
      await load(id, true);
    } catch (e: any) {
      if (version === selectionVersion.current) {
        setError(e.message);
        setSwitching(false);
      }
    }
  }
  async function perform(
    work: () => Promise<any>,
    message: string,
    refresh = true,
  ) {
    setBusy(true);
    setError("");
    setFeedback("");
    try {
      await work();
      if (refresh) await load();
      setFeedback(message);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (!session) return <State error={sessionError} />;
  if (!session.owner) return <Login />;
  const e = bundle?.event;
  return (
    <div className="manage-page" aria-busy={busy || switching}>
      <div className="manage-head">
        <p className="eyebrow">ON TAP COMEDY · OWNER WORKSPACE</p>
        <h1>Make the next night happen.</h1>
        <p className="muted">
          Manage show details, artist applications and your lineup.
        </p>
      </div>
      <div className="toolbar">
        <button
          className="button"
          onClick={() => select("new")}
          disabled={busy}
        >
          + Create show
        </button>
        <a className="button secondary" href="/events" target="_blank">
          View website ↗
        </a>
        <button
          className="button secondary"
          onClick={() =>
            perform(async () => {
              await api("/api/logout", "POST");
              location.replace("/login");
            }, "Signed out")
          }
        >
          Sign out
        </button>
      </div>
      <div className="manage-notices">
        {error && (
          <div className="notice error" role="alert">
            {error}
          </div>
        )}
        {feedback && (
          <div className="feedback" role="status">
            {feedback}
          </div>
        )}
      </div>
      <div className="manage-grid">
        <aside className="sidebar">
          {shows.map((show) => (
            <button
              disabled={busy}
              key={show.id}
              className={selected === show.id ? "active" : ""}
              onClick={() => select(show.id)}
            >
              <strong>{show.title}</strong>
              <small>
                {date(show.starts_at, show.timezone, {
                  weekday: undefined,
                  month: "short",
                  hour: undefined,
                  minute: undefined,
                })}{" "}
                · {show.status}
              </small>
              <small>{show.application_count} applications</small>
            </button>
          ))}
        </aside>
        <div className="manage-editor" inert={switching}>
          {!bundle ? (
            <State error={error} />
          ) : (
            <>
              <div className="toolbar">
                {e.id && (
                  <>
                    <button
                      className="button secondary"
                      disabled={busy}
                      onClick={() =>
                        perform(
                          () => copy(location.origin + eventURL(e.slug)),
                          "Show link copied.",
                          false,
                        )
                      }
                    >
                      Copy show link
                    </button>
                    <button
                      className="button secondary"
                      disabled={busy}
                      onClick={() =>
                        perform(
                          () =>
                            copy(location.origin + eventURL(e.slug) + "/apply"),
                          "Artist signup link copied.",
                          false,
                        )
                      }
                    >
                      Copy signup link
                    </button>
                    <button
                      className="button secondary"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        setError("");
                        try {
                          const d = await api(
                            owner + "/shows/" + e.id + "/duplicate",
                            "POST",
                            {},
                          );
                          await select(d.id);
                          setFeedback(
                            "Created a draft copy. Set the new date before publishing.",
                          );
                        } catch (err: any) {
                          setError(err.message);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      Duplicate show
                    </button>
                  </>
                )}
              </div>
              <div className="tabs">
                {["details", "artists", "lineup"].map((name) => (
                  <button
                    key={name}
                    className={tab === name ? "active" : ""}
                    disabled={!e.id && name !== "details"}
                    onClick={() => setTab(name)}
                  >
                    {name === "details"
                      ? "Show details"
                      : name === "artists"
                        ? `Artists (${bundle.applications.length})`
                        : "Lineup"}
                  </button>
                ))}
              </div>
              {tab === "details" && (
                <Details
                  key={selected}
                  bundle={bundle}
                  busy={busy}
                  save={async (event, settings) => {
                    setBusy(true);
                    setError("");
                    setFeedback("");
                    let id = e.id;
                    try {
                      const saved = await api(
                        owner + "/shows" + (id ? "/" + id : ""),
                        id ? "PATCH" : "POST",
                        { ...event, settings },
                      );
                      id = saved.id;
                      await load(id);
                      setFeedback(
                        event.status === "published"
                          ? settings.applications_open && !settings.cancelled
                            ? "Show saved. Artist applications are enabled, subject to the opening dates and deadline."
                            : "Show saved. Artist applications are switched off."
                          : `Show saved as ${event.status}.`,
                      );
                    } catch (err: any) {
                      setError(err.message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                  venue={async (values) => {
                    const added = await api(owner + "/venues", "POST", values);
                    setBundle((b) =>
                      b ? { ...b, venues: [...b.venues, added] } : b,
                    );
                    return added;
                  }}
                />
              )}
              {tab === "artists" && (
                <>
                  <div className="toolbar">
                    <a
                      className="button secondary"
                      href={owner + "/shows/" + e.id + "/export"}
                    >
                      Download applications CSV ↓
                    </a>
                    <span className="muted">
                      Contact details and notes are private.
                    </span>
                  </div>
                  {bundle.applications.length === 0 ? (
                    <div className="empty">
                      <h3>No applications yet.</h3>
                      <p>
                        Copy the artist signup link and share it for this show.
                      </p>
                    </div>
                  ) : (
                    bundle.applications.map((a: Any) => (
                      <Artist
                        key={a.id}
                        artist={a}
                        busy={busy}
                        scheduled={bundle.slots.some(
                          (s: Any) => s.application_id === a.id,
                        )}
                        review={(status, notes) =>
                          perform(
                            () =>
                              api(owner + "/applications/" + a.id, "PATCH", {
                                status,
                                reviewer_notes: notes,
                              }),
                            "Artist review saved. No message has been sent.",
                          )
                        }
                        schedule={() =>
                          perform(
                            () =>
                              api(owner + "/slots", "POST", {
                                application_id: a.id,
                                starts_at: nextStart(bundle),
                                ends_at: addMinutes(
                                  nextStart(bundle),
                                  bundle.settings.set_minutes,
                                ),
                              }),
                            "Artist added to the lineup. Publish the lineup when it is ready.",
                          )
                        }
                        show={e}
                      />
                    ))
                  )}
                </>
              )}
              {tab === "lineup" && (
                <>
                  <div className="panel">
                    <h3>
                      {bundle.settings.lineup_published
                        ? "The lineup is public"
                        : "The lineup is private"}
                    </h3>
                    <p className="muted">
                      Publishing shows the selected performers and consented
                      photos on this show’s page. Contact details and notes stay
                      private.
                    </p>
                    <button
                      disabled={busy || bundle.settings.cancelled}
                      className="button"
                      onClick={() =>
                        perform(
                          () =>
                            api(
                              owner + "/shows/" + e.id + "/settings",
                              "PATCH",
                              {
                                lineup_published:
                                  !bundle.settings.lineup_published,
                              },
                            ),
                          bundle.settings.lineup_published
                            ? "Lineup hidden."
                            : "Lineup published on the show page.",
                        )
                      }
                    >
                      {bundle.settings.lineup_published
                        ? "Unpublish lineup"
                        : "Publish lineup"}
                    </button>
                  </div>
                  {bundle.slots.length === 0 ? (
                    <div className="empty">
                      <h3>Your stage is waiting.</h3>
                      <p>
                        Add performers from the Artists tab, then arrange the
                        running order here.
                      </p>
                    </div>
                  ) : (
                    bundle.slots.map((s: Any) => (
                      <Slot
                        key={s.id}
                        slot={s}
                        event={e}
                        busy={busy}
                        save={(values) =>
                          perform(
                            () =>
                              api(owner + "/slots/" + s.id, "PATCH", values),
                            "Slot updated.",
                          )
                        }
                        remove={() =>
                          perform(
                            () => api(owner + "/slots/" + s.id, "DELETE"),
                            "Slot removed. The artist application is retained.",
                          )
                        }
                      />
                    ))
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
function nextStart(b: Any) {
  const ends = b.slots
    .map((s: Any) => s.ends_at)
    .filter(Boolean)
    .sort();
  return ends.at(-1) || b.event.starts_at;
}
function addMinutes(value: string, n: number) {
  if (!value) throw Error("Set the show date and time first.");
  return new Date(new Date(value).getTime() + n * 60000).toISOString();
}
function Details({
  bundle,
  busy,
  save,
  venue,
}: {
  bundle: Any;
  busy: boolean;
  save: (event: Any, settings: Any) => Promise<void>;
  venue: (values: Any) => Promise<Any>;
}) {
  const [e, setE] = useState<Any>(bundle.event),
    [s, setS] = useState<Any>(bundle.settings),
    [newVenue, setNewVenue] = useState(false),
    [venueError, setVenueError] = useState(""),
    [venueBusy, setVenueBusy] = useState(false),
    [formError, setFormError] = useState("");
  useEffect(() => {
    setE(bundle.event);
    setS(bundle.settings);
  }, [bundle.event, bundle.settings]);
  const field = (key: string, value: any) => setE({ ...e, [key]: value }),
    setting = (key: string, value: any) => setS({ ...s, [key]: value });
  return (
    <>
      <form
        onSubmit={async (ev) => {
          ev.preventDefault();
          setFormError("");
          try {
            const form = new FormData(ev.currentTarget);
            const clean = { ...e };
            for (const key of [
              "title",
              "status",
              "visibility",
              "timezone",
              "description",
              "external_checkout_url",
            ])
              clean[key] = String(form.get(key) || "");
            clean.capacity = Number(form.get("capacity"));
            clean.venue_id = Number(form.get("venue_id")) || null;
            for (const key of ["starts_at", "ends_at"])
              clean[key] = utcTime(String(form.get(key) || ""), clean.timezone);
            const settings = { ...s };
            for (const key of [
              "applications_open",
              "email_required",
              "cancelled",
            ])
              settings[key] = form.has(key);
            for (const key of ["performer_capacity", "set_minutes"])
              settings[key] = Number(form.get(key));
            for (const key of ["instructions", "cancellation_reason"])
              settings[key] = String(form.get(key) || "");
            for (const key of ["opens_at", "closes_at"])
              settings[key] = utcTime(
                String(form.get(key) || ""),
                clean.timezone,
              );
            for (const k of [
              "id",
              "project_id",
              "created_at",
              "updated_at",
              "application_count",
              "slot_count",
              "ticket_count",
            ])
              delete clean[k];
            if (!clean.id && !clean.slug)
              clean.slug =
                clean.title
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, "") +
                "-" +
                (clean.starts_at
                  ? clean.starts_at.slice(0, 10)
                  : crypto.randomUUID().slice(0, 8));
            await save(clean, settings);
          } catch (err: any) {
            setFormError(err.message);
          }
        }}
      >
        <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="panel">
            <h3>The show</h3>
            <label>
              Show title
              <input
                name="title"
                required
                value={e.title}
                onChange={(ev) => field("title", ev.target.value)}
              />
            </label>
            {e.id && <p className="hint">Permanent link: /events/{e.slug}</p>}
            <div className="form-grid">
              <label>
                Status
                <select
                  name="status"
                  value={e.status}
                  onChange={(ev) => field("status", ev.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="closed">Closed</option>
                  <option value="archived">Archived</option>
                </select>
              </label>
              <label>
                Visibility
                <select
                  name="visibility"
                  value={e.visibility}
                  onChange={(ev) => field("visibility", ev.target.value)}
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </label>
              <label>
                Timezone
                <select
                  name="timezone"
                  value={e.timezone}
                  onChange={(ev) => field("timezone", ev.target.value)}
                >
                  {[
                    ...new Set([
                      "Europe/Madrid",
                      "Europe/London",
                      "Europe/Paris",
                      "UTC",
                      "America/New_York",
                      e.timezone,
                    ]),
                  ].map((tz) => (
                    <option key={tz}>{tz}</option>
                  ))}
                </select>
              </label>
              <label>
                Audience capacity <span>(0 = unlimited)</span>
                <input
                  name="capacity"
                  type="number"
                  min="0"
                  value={e.capacity || 0}
                  onChange={(ev) => field("capacity", Number(ev.target.value))}
                />
              </label>
              <label>
                Show starts
                <input
                  type="datetime-local"
                  name="starts_at"
                  defaultValue={localTime(e.starts_at, e.timezone)}
                  required={e.status === "published"}
                />
              </label>
              <label>
                Show ends
                <input
                  type="datetime-local"
                  name="ends_at"
                  defaultValue={localTime(e.ends_at, e.timezone)}
                  required={e.status === "published"}
                />
              </label>
            </div>
            <p className="muted">Changing the start time moves scheduled artist slots by the same amount. Check application opening and closing dates when rescheduling.</p>
            <label>
              Venue
              <select
                name="venue_id"
                value={e.venue_id || ""}
                onChange={(ev) =>
                  field("venue_id", Number(ev.target.value) || null)
                }
              >
                <option value="">To be announced</option>
                {bundle.venues.map((v: Any) => (
                  <option key={v.id} value={v.id}>
                    {v.name} · {v.city}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="button secondary"
              onClick={() => setNewVenue(!newVenue)}
            >
              + Add venue
            </button>
            <label style={{ marginTop: 22 }}>
              Show description
              <textarea
                name="description"
                value={e.description || ""}
                rows={5}
                onChange={(ev) => field("description", ev.target.value)}
              />
            </label>
            <label>
              Audience ticket link <span>(optional)</span>
              <input
                name="external_checkout_url"
                type="url"
                value={e.external_checkout_url || ""}
                placeholder="https://…"
                onChange={(ev) =>
                  field("external_checkout_url", ev.target.value)
                }
              />
            </label>
          </div>
          <div className="panel">
            <h3>Artist applications</h3>
            <label className="check">
              <input
                name="applications_open"
                type="checkbox"
                checked={s.applications_open || false}
                onChange={(ev) =>
                  setting("applications_open", ev.target.checked)
                }
              />
              Accept artist applications while this show is published
            </label>
            <p>
              Enabled by default for new shows. Opening dates apply only while
              this switch is on; switch it off to pause applications.
            </p>
            <div className="form-grid">
              <label>
                Applications open from <span>(optional)</span>
                <input
                  type="datetime-local"
                  name="opens_at"
                  defaultValue={localTime(s.opens_at, e.timezone)}
                />
              </label>
              <label>
                Application deadline <span>(optional)</span>
                <input
                  type="datetime-local"
                  name="closes_at"
                  defaultValue={localTime(s.closes_at, e.timezone)}
                />
              </label>
              <label>
                Performer spots <span>(0 = unlimited)</span>
                <input
                  name="performer_capacity"
                  type="number"
                  min="0"
                  value={s.performer_capacity || 0}
                  onChange={(ev) =>
                    setting("performer_capacity", Number(ev.target.value))
                  }
                />
              </label>
              <label>
                Minutes per set
                <input
                  name="set_minutes"
                  type="number"
                  min="1"
                  max="120"
                  required
                  value={s.set_minutes || 5}
                  onChange={(ev) =>
                    setting("set_minutes", Number(ev.target.value))
                  }
                />
              </label>
            </div>
            <label className="check">
              <input
                name="email_required"
                type="checkbox"
                checked={s.email_required || false}
                onChange={(ev) => setting("email_required", ev.target.checked)}
              />
              Require email in addition to optional WhatsApp and Instagram
            </label>
            <label>
              Instructions for performers
              <textarea
                name="instructions"
                rows={5}
                value={s.instructions || ""}
                onChange={(ev) => setting("instructions", ev.target.value)}
              />
            </label>
            <p className="hint">
              Performer spots limit the lineup. Applications can stay open for
              waitlist consideration until you close them.
            </p>
          </div>
          <div className="panel">
            <h3>Cancellation</h3>
            <label className="check">
              <input
                name="cancelled"
                type="checkbox"
                checked={s.cancelled || false}
                onChange={(ev) => setting("cancelled", ev.target.checked)}
              />
              Mark this show as cancelled
            </label>
            <p className="hint">
              Cancellation closes applications and hides the lineup. Published
              links continue showing the cancellation notice.
            </p>
            {s.cancelled && (
              <label>
                Public cancellation message
                <textarea
                  name="cancellation_reason"
                  value={s.cancellation_reason || ""}
                  onChange={(ev) =>
                    setting("cancellation_reason", ev.target.value)
                  }
                />
              </label>
            )}
          </div>
          {formError && (
            <div className="notice error" role="alert">
              {formError}
            </div>
          )}
          <button className="button">
            {busy ? "Saving…" : e.id ? "Save show" : "Create show"}
          </button>
        </fieldset>
      </form>
      {newVenue && (
        <form
          className="panel"
          style={{ marginTop: 25 }}
          onSubmit={async (ev) => {
            ev.preventDefault();
            setVenueBusy(true);
            setVenueError("");
            try {
              const values = Object.fromEntries(new FormData(ev.currentTarget));
              const added = await venue(values);
              field("venue_id", added.id);
              setNewVenue(false);
            } catch (err: any) {
              setVenueError(err.message);
            } finally {
              setVenueBusy(false);
            }
          }}
        >
          <h3>Add a venue</h3>
          <label>
            Name
            <input name="name" required />
          </label>
          <label>
            Address
            <input name="address" />
          </label>
          <div className="form-grid">
            <label>
              City
              <input name="city" />
            </label>
            <label>
              Country
              <input name="country" />
            </label>
          </div>
          {venueError && <div className="notice error">{venueError}</div>}
          <button className="button" disabled={venueBusy}>
            {venueBusy ? "Adding…" : "Add venue"}
          </button>
        </form>
      )}
    </>
  );
}
function Artist({
  artist: a,
  busy,
  scheduled,
  review,
  schedule,
  show,
}: {
  artist: Any;
  busy: boolean;
  scheduled: boolean;
  review: (status: string, notes: string) => Promise<void>;
  schedule: () => Promise<void>;
  show: Any;
}) {
  const [status, setStatus] = useState(a.status),
    [notes, setNotes] = useState(a.reviewer_notes || ""),
    [copied, setCopied] = useState(false);
  useEffect(() => {
    setStatus(a.status);
    setNotes(a.reviewer_notes || "");
  }, [a.status, a.reviewer_notes]);
  let social: Any = {};
  try {
    social = JSON.parse(a.social_links_json);
  } catch {}
  return (
    <article className="artist-row">
      <div className="summary">
        {social.has_photo && (
          <img
            src={owner + "/applications/" + a.id + "/photo"}
            alt={a.stage_name || a.applicant_name}
          />
        )}
        <div>
          <h3>{a.stage_name || a.applicant_name}</h3>
          <span className="tag">{a.status}</span>
          <p>
            {a.applicant_name} · {a.email || "No email"}{" "}
            {a.phone && "· WhatsApp +" + a.phone}{" "}
            {social.instagram && "· @" + social.instagram}
          </p>
          <p>{a.bio}</p>
          {a.tech_needs && <p>Performer notes: {a.tech_needs}</p>}
          {social.has_photo && (
            <a
              className="muted"
              href={owner + "/applications/" + a.id + "/photo?download=1"}
            >
              Download photo ↓
            </a>
          )}
        </div>
      </div>
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          const values = new FormData(ev.currentTarget);
          review(
            String(values.get("status")),
            String(values.get("reviewer_notes") || ""),
          );
        }}
      >
        <div className="form-grid" style={{ marginTop: 20 }}>
          <label>
            Decision
            <select
              name="status"
              value={status}
              onChange={(ev) => setStatus(ev.target.value)}
            >
              {[
                "submitted",
                "shortlisted",
                "accepted",
                "rejected",
                "withdrawn",
              ].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <label>
            Private review notes
            <textarea
              rows={2}
              name="reviewer_notes"
              value={notes}
              onChange={(ev) => setNotes(ev.target.value)}
            />
          </label>
        </div>
        <div className="toolbar">
          <button className="button secondary" disabled={busy}>
            Save review
          </button>
          <button
            type="button"
            className="button secondary"
            disabled={busy || scheduled}
            onClick={schedule}
          >
            {scheduled ? "On the lineup" : "Accept & add to lineup"}
          </button>
          <button
            type="button"
            className="button secondary"
            onClick={async () => {
              await copy(
                `Hi ${a.stage_name || a.applicant_name}, an update on your application for ${show.title} (${date(show.starts_at, show.timezone)}): ${a.status === "accepted" ? "we’d love to have you on stage!" : a.status === "rejected" ? "we can’t offer a spot this time. Thank you for applying." : "your application is being reviewed. We’ll confirm when the lineup is ready."}`,
              );
              setCopied(true);
            }}
          >
            {copied ? "Message copied" : "Copy status message"}
          </button>
        </div>
      </form>
    </article>
  );
}
function Slot({
  slot: s,
  event: e,
  busy,
  save,
  remove,
}: {
  slot: Any;
  event: Any;
  busy: boolean;
  save: (values: Any) => Promise<void>;
  remove: () => Promise<void>;
}) {
  const [start, setStart] = useState(localTime(s.starts_at, e.timezone)),
    [end, setEnd] = useState(localTime(s.ends_at, e.timezone)),
    [name, setName] = useState(s.performer_name),
    [error, setError] = useState("");
  return (
    <form
      className="panel"
      onSubmit={(ev) => {
        ev.preventDefault();
        setError("");
        try {
          save({
            performer_name: String(
              new FormData(ev.currentTarget).get("performer_name") || "",
            ),
            starts_at: utcTime(
              String(new FormData(ev.currentTarget).get("starts_at") || ""),
              e.timezone,
            ),
            ends_at: utcTime(
              String(new FormData(ev.currentTarget).get("ends_at") || ""),
              e.timezone,
            ),
          });
        } catch (err: any) {
          setError(err.message);
        }
      }}
    >
      <label>
        Performer
        <input
          required
          name="performer_name"
          value={name}
          onChange={(ev) => setName(ev.target.value)}
        />
      </label>
      <div className="form-grid">
        <label>
          Start ({e.timezone})
          <input
            type="datetime-local"
            required
            name="starts_at"
            defaultValue={start}
          />
        </label>
        <label>
          End
          <input
            type="datetime-local"
            required
            name="ends_at"
            defaultValue={end}
          />
        </label>
      </div>
      {error && <div className="notice error">{error}</div>}
      <div className="toolbar">
        <button className="button secondary" disabled={busy}>
          Save slot
        </button>
        <button
          type="button"
          disabled={busy}
          className="button secondary danger"
          onClick={remove}
        >
          Remove from lineup
        </button>
      </div>
    </form>
  );
}
